import { useCallback, useEffect, useRef, useState } from 'react';

import type {
  EncryptionInfoContent,
  EncryptionStepItem,
  FileUploadStep,
} from '@/components/organism/FileUploadModal/FileUploadDialogV2.types';
import { MIN_LEN } from '@/constants';
import { useAppDispatch, useAppSelector } from '@/store';
import { selectSixId } from '@/store/selectors/sixDetail';
import { clearUploadProgress } from '@/store/slices/poc/heliaSlice';
import { retryFileAnnouncementThunk } from '@/store/thunks/poc/helia';
import { selectIsFileUploadModal } from '@/store/selectors/ui';
import { hideModal } from '@/store/thunks/ui/uiThunk';
import { uploadFileThunk } from '@/store/thunks/sixDetail/uploadFileThunk';
import type { UploadProgress } from '@/types/poc/helia';
import { showSuccessToast } from '@/utils/toast.utils';

const ENCRYPTION_STEP_LABELS: string[] = [
  'Uploading file...',
  'Creating transport key for file...',
  'Encrypting file using transport key...',
  'Encrypting transport key...',
  'Placing encrypted file and key in secure high-availability state...',
  'Creating shared secret key for each SIX participant...',
  'File ready for all SIX participants.',
];

const STEP_INDEX_ENCRYPTING = 0;
const STEP_INDEX_UPLOADING = 1;
const STEP_INDEX_ANNOUNCING = 3;
const STEP_INDEX_HAP_DOWNLOADING = 4;
const STEP_INDEX_HAP_REGISTERING = 5;
const MAX_ANNOUNCEMENT_RETRIES = 3;

const ENCRYPTION_INFO: EncryptionInfoContent = {
  title: 'About Encrypted Uploads',
  description: 'Files are encrypted using a secure multi-step process:',
  steps: [
    'Upload file',
    'Create transport key for file',
    'Encrypt file using transport key',
    'Encrypt transport key',
    'Place encrypted file and key in secure high-availability state',
    'Create shared secret key for each SIX participant',
    'File ready for all SIX participants',
  ],
  footerNote:
    'Your files are end-to-end encrypted and securely shared with all SIX participants.',
};

function buildInitialEncryptionSteps(): EncryptionStepItem[] {
  return ENCRYPTION_STEP_LABELS.map((label, index) => ({
    id: String(index),
    label,
    status: 'pending',
  }));
}

interface UseFileUploadModalState {
  isOpen: boolean;
  step: FileUploadStep;
  files: File[];
  encryptionSteps: EncryptionStepItem[];
  uploadedFileCount: number;
  isUploadDisabled: boolean;
  encryptionInfo: EncryptionInfoContent;
  currentUpload: UploadProgress | null;
  currentUploadFileName: string;
  currentUploadError: string | null;
  canRetryCurrentUpload: boolean;
  isUploading: boolean;
}

interface UseFileUploadModalHandlers {
  handleClose: () => void;
  handleFilesAdded: (newFiles: File[]) => void;
  handleFileRemoved: (index: number) => void;
  handleUpload: () => void;
  handleRetryCurrentUpload: () => void;
  handleDone: () => void;
}

interface UseFileUploadModalReturn {
  state: UseFileUploadModalState;
  handlers: UseFileUploadModalHandlers;
}

// eslint-disable-next-line complexity -- coordinates modal upload queue progression, observed Redux progress, and retry handling
export function useFileUploadModal(): UseFileUploadModalReturn {
  const dispatch = useAppDispatch();

  const isOpen = useAppSelector(selectIsFileUploadModal);
  const sixId = useAppSelector(selectSixId);
  const heliaUploadProgress = useAppSelector((state) => state.helia.uploadProgress);

  const [files, setFiles] = useState<File[]>([]);
  const [step, setStep] = useState<FileUploadStep>('select');
  const [encryptionSteps, setEncryptionSteps] = useState<EncryptionStepItem[]>(
    buildInitialEncryptionSteps,
  );
  const [uploadedFileCount, setUploadedFileCount] = useState(MIN_LEN);
  const [isUploading, setIsUploading] = useState(false);
  const [currentUploadId, setCurrentUploadId] = useState<string | null>(null);
  const [currentUploadIndex, setCurrentUploadIndex] = useState<number | null>(null);
  const [currentUploadError, setCurrentUploadError] = useState<string | null>(null);

  const isUploadingRef = useRef(false);
  const startedUploadIdsRef = useRef<Set<string>>(new Set());
  const terminalStateKeyRef = useRef<string | null>(null);

  const clearTrackedUpload = useCallback((uploadId: string): void => {
    dispatch(clearUploadProgress(uploadId));
    startedUploadIdsRef.current.delete(uploadId);
  }, [dispatch]);

  const resetState = useCallback((): void => {
    startedUploadIdsRef.current.forEach((uploadId) => {
      dispatch(clearUploadProgress(uploadId));
    });
    startedUploadIdsRef.current.clear();
    setFiles([]);
    setStep('select');
    setEncryptionSteps(buildInitialEncryptionSteps());
    setUploadedFileCount(MIN_LEN);
    setIsUploading(false);
    setCurrentUploadId(null);
    setCurrentUploadIndex(null);
    setCurrentUploadError(null);
    isUploadingRef.current = false;
    terminalStateKeyRef.current = null;
  }, [dispatch]);

  const handleClose = useCallback((): void => {
    if (isUploadingRef.current) return;
    dispatch(hideModal());
    resetState();
  }, [dispatch, resetState]);

  const handleFilesAdded = useCallback((newFiles: File[]): void => {
    setFiles((previous) => [...previous, ...newFiles]);
    setStep('review');
  }, []);

  const handleFileRemoved = useCallback((index: number): void => {
    setFiles((previous) => {
      const updated = previous.filter((_, fileIndex) => fileIndex !== index);
      if (updated.length === MIN_LEN) {
        setStep('select');
      }
      return updated;
    });
  }, []);

  const setActiveEncryptionStep = useCallback(
    (stepIndex: number): void => {
      setEncryptionSteps((previous) =>
        previous.map((encStep, index) => {
          if (index < stepIndex) return { ...encStep, status: 'success' };
          if (index === stepIndex) return { ...encStep, status: 'loading' };
          return encStep;
        }),
      );
    },
    [],
  );

  const markAllEncryptionStepsComplete = useCallback((): void => {
    setEncryptionSteps((previous) =>
      previous.map((encStep) => ({ ...encStep, status: 'success' })),
    );
  }, []);

  const markEncryptionStepsError = useCallback((): void => {
    setEncryptionSteps((previous) =>
      previous.map((encStep) => {
        if (encStep.status === 'loading') return { ...encStep, status: 'error' };
        if (encStep.status === 'pending') return encStep;
        return encStep;
      }),
    );
  }, []);

  const updateModalStepsFromStage = useCallback((stage: string): void => {
    if (stage === 'encrypting') {
      setActiveEncryptionStep(STEP_INDEX_ENCRYPTING);
      return;
    }

    if (stage === 'uploading') {
      setActiveEncryptionStep(STEP_INDEX_UPLOADING);
      return;
    }

    if (stage === 'announcing') {
      setActiveEncryptionStep(STEP_INDEX_ANNOUNCING);
      return;
    }

    if (stage === 'hap-downloading') {
      setActiveEncryptionStep(STEP_INDEX_HAP_DOWNLOADING);
      return;
    }

    if (stage === 'hap-registering') {
      setActiveEncryptionStep(STEP_INDEX_HAP_REGISTERING);
      return;
    }

    if (stage === 'complete') {
      markAllEncryptionStepsComplete();
    }
  }, [markAllEncryptionStepsComplete, setActiveEncryptionStep]);

  const startUploadAtIndex = useCallback(
    (fileIndex: number): void => {
      if (sixId === undefined) return;

      const nextFile = files[fileIndex];

      const uploadId = crypto.randomUUID();
      startedUploadIdsRef.current.add(uploadId);
      terminalStateKeyRef.current = null;
      setCurrentUploadId(uploadId);
      setCurrentUploadIndex(fileIndex);
      setCurrentUploadError(null);
      setIsUploading(true);
      isUploadingRef.current = true;
      setActiveEncryptionStep(STEP_INDEX_ENCRYPTING);

      void dispatch(uploadFileThunk({ sixId, file: nextFile, uploadId }))
        .unwrap()
        .catch((error: unknown) => {
          const message = error instanceof Error ? error.message : 'Failed to upload file';
          markEncryptionStepsError();
          setCurrentUploadError(message);
          setIsUploading(false);
          isUploadingRef.current = false;
        });
    },
    [dispatch, files, markEncryptionStepsError, setActiveEncryptionStep, sixId],
  );

  useEffect(() => {
    if (currentUploadId == null || currentUploadIndex == null) {
      return;
    }

    if (!Object.prototype.hasOwnProperty.call(heliaUploadProgress, currentUploadId)) {
      return;
    }

    const entry = heliaUploadProgress[currentUploadId];
    updateModalStepsFromStage(entry.stage);
    const terminalStateKey = `${currentUploadId}:${entry.stage}:${String(entry.retryCount ?? MIN_LEN)}`;

    if (entry.stage === 'error') {
      if (terminalStateKeyRef.current === terminalStateKey) {
        return;
      }

      terminalStateKeyRef.current = terminalStateKey;
      markEncryptionStepsError();
      setCurrentUploadError(entry.error ?? `Failed to upload ${entry.fileName}`);
      setIsUploading(false);
      isUploadingRef.current = false;
      return;
    }

    if (entry.stage !== 'complete') {
      return;
    }

    if (terminalStateKeyRef.current === terminalStateKey) {
      return;
    }

    terminalStateKeyRef.current = terminalStateKey;
    setCurrentUploadError(null);
    markAllEncryptionStepsComplete();

    const completedCount = currentUploadIndex + 1;
    setUploadedFileCount(completedCount);

    if (completedCount >= files.length) {
      setIsUploading(false);
      isUploadingRef.current = false;
      showSuccessToast(
        files.length === 1 ? 'File uploaded successfully' : `${String(files.length)} files uploaded successfully`,
      );
      return;
    }

    clearTrackedUpload(currentUploadId);
    setEncryptionSteps(buildInitialEncryptionSteps());
    startUploadAtIndex(completedCount);
  }, [
    clearTrackedUpload,
    currentUploadId,
    currentUploadIndex,
    files.length,
    heliaUploadProgress,
    markAllEncryptionStepsComplete,
    markEncryptionStepsError,
    startUploadAtIndex,
    updateModalStepsFromStage,
  ]);

  const handleUpload = useCallback((): void => {
    if (sixId === undefined || files.length === MIN_LEN || isUploadingRef.current) return;

    setStep('progress');
    setEncryptionSteps(buildInitialEncryptionSteps());
    setUploadedFileCount(MIN_LEN);
    setCurrentUploadError(null);
    startUploadAtIndex(MIN_LEN);
  }, [files.length, sixId, startUploadAtIndex]);

  const handleRetryCurrentUpload = useCallback((): void => {
    if (currentUploadId == null) return;

    setCurrentUploadError(null);
    terminalStateKeyRef.current = null;
    setIsUploading(true);
    isUploadingRef.current = true;

    const entry = heliaUploadProgress[currentUploadId];
    const canRetryAnnouncement =
      entry.stage === 'error' && (entry.retryCount ?? MIN_LEN) < MAX_ANNOUNCEMENT_RETRIES;

    if (canRetryAnnouncement) {
      void dispatch(retryFileAnnouncementThunk({ uploadId: currentUploadId }));
      return;
    }

    if (currentUploadIndex == null) {
      setIsUploading(false);
      isUploadingRef.current = false;
      return;
    }

    clearTrackedUpload(currentUploadId);
    setEncryptionSteps(buildInitialEncryptionSteps());
    startUploadAtIndex(currentUploadIndex);
  }, [
    clearTrackedUpload,
    currentUploadId,
    currentUploadIndex,
    dispatch,
    heliaUploadProgress,
    startUploadAtIndex,
  ]);

  const handleDone = useCallback((): void => {
    dispatch(hideModal());
    resetState();
  }, [dispatch, resetState]);

  const resolvedStep: FileUploadStep =
    step === 'review' && files.length === MIN_LEN ? 'select' : step;
  const currentUpload = currentUploadId == null ? null : heliaUploadProgress[currentUploadId] ?? null;
  const currentUploadFileName =
    currentUpload?.fileName ??
    (currentUploadIndex == null ? '' : files[currentUploadIndex]?.name ?? '');
  const canRetryCurrentUpload =
    (currentUpload?.stage === 'error' || currentUploadError !== null) &&
    (
      (currentUpload?.retryCount ?? MIN_LEN) < MAX_ANNOUNCEMENT_RETRIES ||
      currentUpload == null
    );

  return {
    state: {
      isOpen,
      step: resolvedStep,
      files,
      encryptionSteps,
      uploadedFileCount,
      isUploadDisabled: isUploading || files.length === MIN_LEN,
      encryptionInfo: ENCRYPTION_INFO,
      currentUpload,
      currentUploadFileName,
      currentUploadError,
      canRetryCurrentUpload,
      isUploading,
    },
    handlers: {
      handleClose,
      handleFilesAdded,
      handleFileRemoved,
      handleUpload,
      handleRetryCurrentUpload,
      handleDone,
    },
  };
}
